import { useEffect, useRef, useState, type HTMLAttributes } from "react";

/**
 * CSS + IntersectionObserver reveal — no framer-motion dependency.
 * Same API as before (delay, y, className, children, ...divProps).
 */
export function Reveal({
  children,
  delay = 0,
  y = 24,
  className = "",
  style,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { delay?: number; y?: number }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "-80px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translate3d(0,0,0)" : `translate3d(0,${y}px,0)`,
        transition: `opacity 700ms cubic-bezier(0.2,0.8,0.2,1) ${delay}s, transform 700ms cubic-bezier(0.2,0.8,0.2,1) ${delay}s`,
        willChange: shown ? undefined : "opacity, transform",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}